import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, FlaskConical, Mail, RotateCw, Send } from 'lucide-react';
import api from '../../services/api';
import { ConfirmDialog, CountBadge, KpiGrid, KpiTile, PageHeader } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { addDaysToYmd, getUpcomingWeekendStartEt, getWeekendStartForYmd } from '../../utils/easternTime';
import { MISSING, formatCount, formatDate, formatDateRange, formatNumber, formatTime } from '../../utils/format';
import CoverageTab from './weekend-shifts/CoverageTab';
import HistoryTab from './weekend-shifts/HistoryTab';
import RepSheet from './weekend-shifts/RepSheet';
import RosterTab from './weekend-shifts/RosterTab';
import SuspensionsTab from './weekend-shifts/SuspensionsTab';
import {
  asSentence,
  countDueCheckpoints,
  countWeekends,
  firstName,
  formatWeekendRange,
  formatWeekendSpan,
  getHolidayDayLabel,
  hasMissedAny,
  hasMissedEnd,
  hasMissedStart,
  isUncappedHolidayShiftKey,
  shiftListLabel,
} from './weekend-shifts/helpers';
import type {
  RosterFilter,
  WeekendHolidayDay,
  WeekendShiftAnalyticsData,
  WeekendShiftRosterData,
  WeekendShiftRosterRow,
  WeekendShiftSuspensionStatus,
} from './weekend-shifts/types';

type ManagerTab = 'roster' | 'coverage' | 'suspensions' | 'history';
type EmailConfirm = 'policy' | 'digest' | 'ops';
type TestEmailKind = 'policy' | 'digest';
type RepConfirm = 'suspend' | 'unsuspend' | 'removeShifts' | 'removeHoliday';

const DEFAULT_TEST_RECIPIENT = 'ebrown@sbgfunding.com';

/**
 * What went wrong with a load, in words that point the right way: the API's
 * own message when it sent one; otherwise no response at all, an error
 * status, or a problem on this page before any request went out.
 */
const describeLoadError = (err: any): string => {
  const apiMessage = err?.response?.data?.error;
  if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage;
  if (err?.response) {
    const status = err.response.status;
    return status
      ? `The server returned an error (status ${status}). Try again in a minute.`
      : 'The server returned an error. Try again in a minute.';
  }
  if (err?.request || err?.isAxiosError) return 'The server didn’t respond. Check your connection and try again.';
  return 'This page hit a problem reading the data. Reload the page and try again.';
};

const getPreviousWeekendStartEt = () => addDaysToYmd(getUpcomingWeekendStartEt(), -7);
const getRangeStartForWeekendCount = (endWeekendDate: string, weekendCount: number) =>
  addDaysToYmd(endWeekendDate, -7 * Math.max(weekendCount - 1, 0));

const sum = (values: number[]) => values.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);

/* ------------------------------------------------------------------------ */
/* Header controls                                                           */
/* ------------------------------------------------------------------------ */

const WeekendStepper: React.FC<{
  label: string;
  isUpcoming: boolean;
  selectedWeekendStart: string;
  onPrevious: () => void;
  onNext: () => void;
  onPick: (value: string) => void;
  onUpcoming: () => void;
}> = ({ label, isUpcoming, selectedWeekendStart, onPrevious, onNext, onPick, onUpcoming }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <Box
      role="group"
      aria-label="Weekend"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        p: '1px',
        border: `1px solid ${sbg.action.secondaryBorder}`,
        borderRadius: sbg.radius.pill,
        bgcolor: sbg.bg.surface,
      }}
    >
      <Tooltip title="Previous weekend">
        <IconButton size="small" aria-label="Previous weekend" onClick={onPrevious}>
          <ChevronLeft size={18} />
        </IconButton>
      </Tooltip>
      <Button
        size="small"
        variant="text"
        startIcon={<CalendarDays />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor) || undefined}
        aria-controls={anchor ? 'wsm-weekend-picker' : undefined}
        aria-label={`Choose weekend, showing ${label}`}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {label}
      </Button>
      <Tooltip title="Next weekend">
        <IconButton size="small" aria-label="Next weekend" onClick={onNext}>
          <ChevronRight size={18} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { id: 'wsm-weekend-picker', role: 'dialog', 'aria-label': 'Choose a weekend', sx: { mt: 0.5 } },
        }}
      >
        <Box sx={{ p: 2, width: 288, display: 'grid', gap: 1.5 }}>
          <TextField
            type="date"
            label="Jump to a weekend"
            value={selectedWeekendStart}
            onChange={(event) => {
              if (event.target.value) onPick(event.target.value);
            }}
            helperText="Pick any day. The roster opens the weekend it belongs to."
            autoFocus
            fullWidth
          />
          {!isUpcoming && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onUpcoming();
                setAnchor(null);
              }}
            >
              Go to upcoming weekend
            </Button>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

const TestEmailDialog: React.FC<{
  kind: TestEmailKind | null;
  address: string;
  onAddressChange: (value: string) => void;
  sending: boolean;
  onClose: () => void;
  onSend: () => void;
}> = ({ kind, address, onAddressChange, sending, onClose, onSend }) => {
  const isPolicy = kind === 'policy';
  return (
    <Dialog open={Boolean(kind)} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="wsm-test-email-title">
      <DialogTitle id="wsm-test-email-title">{isPolicy ? 'Send a policy update test' : 'Send a sample team digest'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="body1" sx={{ color: sbg.text.secondary }}>
          {isPolicy
            ? 'One copy of the rep email goes to this address. No reps are emailed.'
            : 'The first manager’s digest, alphabetically, goes to this address so you can see what managers receive. No managers are emailed.'}
        </Typography>
        <TextField
          label="Send to"
          type="email"
          value={address}
          onChange={(event) => onAddressChange(event.target.value)}
          placeholder="e.g. name@sbgfunding.com"
          autoFocus
          fullWidth
          onKeyDown={(event) => {
            if (event.key === 'Enter' && address.trim() && !sending) onSend();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSend} disabled={sending || !address.trim()} aria-busy={sending || undefined}>
          {sending ? 'Sending…' : isPolicy ? 'Send test email' : 'Send sample digest'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

const WeekendShiftManager: React.FC = () => {
  const upcomingWeekendStart = useMemo(() => getUpcomingWeekendStartEt(), []);
  const defaultHistoryEnd = useMemo(() => getPreviousWeekendStartEt(), []);
  const [selectedWeekendStart, setSelectedWeekendStart] = useState(upcomingWeekendStart);
  const [historyStartWeekend, setHistoryStartWeekend] = useState(getRangeStartForWeekendCount(defaultHistoryEnd, 8));
  const [historyEndWeekend, setHistoryEndWeekend] = useState(defaultHistoryEnd);
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');
  const [rosterData, setRosterData] = useState<WeekendShiftRosterData | null>(null);
  const [analyticsData, setAnalyticsData] = useState<WeekendShiftAnalyticsData | null>(null);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const [rosterUpdatedAt, setRosterUpdatedAt] = useState<Date | null>(null);
  const [analyticsUpdatedAt, setAnalyticsUpdatedAt] = useState<Date | null>(null);
  const [sendingOps, setSendingOps] = useState(false);
  const [sendingPolicyUpdate, setSendingPolicyUpdate] = useState(false);
  const [sendingManagerDigest, setSendingManagerDigest] = useState(false);
  const [actionResult, setActionResult] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [draftShiftKeys, setDraftShiftKeys] = useState<string[]>([]);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [applyingSuspension, setApplyingSuspension] = useState(false);
  const [clearingSuspension, setClearingSuspension] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [holidayLabelDraft, setHolidayLabelDraft] = useState('');
  const [holidayDayDraft, setHolidayDayDraft] = useState<WeekendHolidayDay>('monday');
  const [savingHoliday, setSavingHoliday] = useState(false);
  // Presentation-only state: tabs, menus and confirmations.
  const [activeTab, setActiveTab] = useState<ManagerTab>('roster');
  const [rosterShiftFilter, setRosterShiftFilter] = useState<string | null>(null);
  const [emailMenuAnchor, setEmailMenuAnchor] = useState<HTMLElement | null>(null);
  const [emailConfirm, setEmailConfirm] = useState<EmailConfirm | null>(null);
  const [testEmailKind, setTestEmailKind] = useState<TestEmailKind | null>(null);
  const [testEmailAddress, setTestEmailAddress] = useState(DEFAULT_TEST_RECIPIENT);
  const [repConfirm, setRepConfirm] = useState<RepConfirm | null>(null);

  const fetchRoster = useCallback(async (weekendStartDate = selectedWeekendStart) => {
    setRosterLoading(true);
    setRosterError(null);
    try {
      const response = await api.get('/weekend-shifts/admin/upcoming', {
        params: { weekendStartDate },
      });
      setRosterData(response.data?.data || null);
      setRosterUpdatedAt(new Date());
    } catch (err: any) {
      setRosterError(describeLoadError(err));
    } finally {
      setRosterLoading(false);
    }
  }, [selectedWeekendStart]);

  const fetchAnalytics = useCallback(async (
    startWeekendDate = historyStartWeekend,
    endWeekendDate = historyEndWeekend
  ) => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const response = await api.get('/weekend-shifts/admin/analytics', {
        params: { startWeekendDate, endWeekendDate },
      });
      setAnalyticsData(response.data?.data || null);
      setAnalyticsUpdatedAt(new Date());
    } catch (err: any) {
      setAnalyticsError(describeLoadError(err));
    } finally {
      setAnalyticsLoading(false);
    }
  }, [historyEndWeekend, historyStartWeekend]);

  useEffect(() => {
    fetchRoster(selectedWeekendStart);
  }, [fetchRoster, selectedWeekendStart]);

  useEffect(() => {
    fetchAnalytics(historyStartWeekend, historyEndWeekend);
  }, [fetchAnalytics, historyEndWeekend, historyStartWeekend]);

  useEffect(() => {
    const holiday = rosterData?.holiday?.holiday;
    if (holiday) {
      setHolidayLabelDraft(holiday.holidayLabel);
      setHolidayDayDraft(holiday.holidayDay === 'friday' ? 'friday' : 'monday');
    } else {
      setHolidayLabelDraft('');
      setHolidayDayDraft('monday');
    }
  }, [rosterData?.holiday?.holiday]);

  const handleRefresh = async () => {
    await Promise.all([
      fetchRoster(selectedWeekendStart),
      fetchAnalytics(historyStartWeekend, historyEndWeekend),
    ]);
  };

  const handleSendOpsSummary = async () => {
    setSendingOps(true);
    setActionResult(null);
    try {
      const response = await api.post('/admin/emails/weekend-shifts/ops-summary');
      setActionResult({
        success: true,
        message: response.data?.message || 'Ops summary sent to Tom and Emma.',
      });
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t send the ops summary. Try again in a minute.',
      });
    } finally {
      setSendingOps(false);
    }
  };

  const handleSendPolicyUpdate = async () => {
    setSendingPolicyUpdate(true);
    setActionResult(null);
    try {
      const response = await api.post('/admin/emails/weekend-shifts/policy-update');
      const sent = response.data?.data?.sent ?? 0;
      const failed = response.data?.data?.failed ?? 0;
      const detail = failed > 0
        ? `${formatCount(sent, 'rep')} emailed; ${formatCount(failed, 'send')} failed. The email log has details.`
        : `${formatCount(sent, 'rep')} emailed.`;
      setActionResult({
        success: failed === 0,
        message: response.data?.message ? `${response.data.message} ${detail}` : `Policy update sent. ${detail}`,
      });
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t send the policy update. Try again in a minute.',
      });
    } finally {
      setSendingPolicyUpdate(false);
    }
  };

  const handleSendPolicyUpdateTest = async (recipient: string) => {
    const testRecipientEmail = recipient.trim();
    if (!testRecipientEmail) return;

    setSendingPolicyUpdate(true);
    setActionResult(null);
    try {
      const response = await api.post('/admin/emails/weekend-shifts/policy-update', {
        testRecipientEmail,
      });
      const sent = response.data?.data?.sent ?? 0;
      const failed = response.data?.data?.failed ?? 0;
      setActionResult({
        success: failed === 0 && sent === 1,
        message: response.data?.message
          || (failed === 0
            ? `Test policy update sent to ${testRecipientEmail}.`
            : `The test policy update to ${testRecipientEmail} didn’t send.`),
      });
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t send the test policy update. Try again in a minute.',
      });
    } finally {
      setSendingPolicyUpdate(false);
    }
  };

  const handleSendManagerDigest = async () => {
    setSendingManagerDigest(true);
    setActionResult(null);
    try {
      const response = await api.post('/admin/emails/weekend-shifts/manager-digest');
      const sent = response.data?.data?.sent ?? 0;
      const failed = response.data?.data?.failed ?? 0;
      const targetedManagers = response.data?.data?.targetedManagers ?? 0;
      const detail = failed > 0
        ? `${formatNumber(sent)} of ${formatCount(targetedManagers, 'manager')} emailed; ${formatCount(failed, 'send')} failed.`
        : `${formatCount(sent, 'manager')} emailed.`;
      setActionResult({
        success: failed === 0 && targetedManagers > 0,
        message: response.data?.message ? `${response.data.message}` : `Team digest sent. ${detail}`,
      });
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t send the team digests. Try again in a minute.',
      });
    } finally {
      setSendingManagerDigest(false);
    }
  };

  const handleSendManagerDigestTest = async (recipient: string) => {
    const testRecipientEmail = recipient.trim();
    if (!testRecipientEmail) return;

    setSendingManagerDigest(true);
    setActionResult(null);
    try {
      const response = await api.post('/admin/emails/weekend-shifts/manager-digest', {
        testRecipientEmail,
      });
      const sent = response.data?.data?.sent ?? 0;
      const failed = response.data?.data?.failed ?? 0;
      setActionResult({
        success: failed === 0 && sent === 1,
        message: response.data?.message
          || (failed === 0
            ? `Sample team digest sent to ${testRecipientEmail}.`
            : `The sample team digest to ${testRecipientEmail} didn’t send.`),
      });
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t send the sample team digest. Try again in a minute.',
      });
    } finally {
      setSendingManagerDigest(false);
    }
  };

  const handleSetHoliday = async (enabled: boolean) => {
    if (!rosterData) return;

    const dayLabel = getHolidayDayLabel(holidayDayDraft);
    setSavingHoliday(true);
    setActionResult(null);
    try {
      const response = await api.post('/weekend-shifts/admin/holiday', {
        weekendStartDate: rosterData.weekendStartDate,
        enabled,
        holidayDay: holidayDayDraft,
        holidayLabel: enabled ? holidayLabelDraft.trim() : '',
      });
      setActionResult({
        success: true,
        message: response.data?.message || (enabled ? `${dayLabel} holiday shifts saved.` : `${dayLabel} holiday shifts removed.`),
      });
      await fetchRoster(rosterData.weekendStartDate);
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || 'Couldn’t update the holiday. Try again in a minute.',
      });
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleQuickRange = (weekendCount: number) => {
    setHistoryStartWeekend(getRangeStartForWeekendCount(historyEndWeekend, weekendCount));
  };

  const handleHistoryStartChange = (nextValue: string) => {
    setHistoryStartWeekend(nextValue);
    if (nextValue > historyEndWeekend) {
      setHistoryEndWeekend(nextValue);
    }
  };

  const handleHistoryEndChange = (nextValue: string) => {
    setHistoryEndWeekend(nextValue);
    if (nextValue < historyStartWeekend) {
      setHistoryStartWeekend(nextValue);
    }
  };

  const selectedRep = useMemo(
    () => rosterData?.roster.find((rep) => rep.employeeId === selectedEmployeeId) || null,
    [rosterData, selectedEmployeeId]
  );

  const updateRosterSuspensionStatus = (
    employeeId: string,
    suspensionStatus: WeekendShiftSuspensionStatus | undefined
  ) => {
    if (!suspensionStatus) return;
    setRosterData((current) => current
      ? {
          ...current,
          roster: current.roster.map((rep) =>
            rep.employeeId === employeeId
              ? { ...rep, suspensionStatus }
              : rep
          ),
        }
      : current
    );
  };

  const suspendedReps = useMemo(
    () => (rosterData?.roster || []).filter((rep) => rep.suspensionStatus?.isSuspended),
    [rosterData]
  );

  const handleOpenRep = (rep: WeekendShiftRosterRow) => {
    setSelectedEmployeeId(rep.employeeId);
    setDraftShiftKeys(rep.selectedShiftKeys);
    setSuspensionReason('');
  };

  const handleCloseRep = () => {
    if (savingAssignment || applyingSuspension || clearingSuspension) return;
    setSelectedEmployeeId(null);
    setDraftShiftKeys([]);
    setSuspensionReason('');
  };

  const handleToggleDraftShift = (shiftKey: string) => {
    setDraftShiftKeys((current) =>
      current.includes(shiftKey)
        ? current.filter((key) => key !== shiftKey)
        : [...current, shiftKey]
    );
  };

  const handleSaveAssignment = async () => {
    if (!selectedRep || !rosterData) return;

    setSavingAssignment(true);
    setActionResult(null);
    try {
      const response = await api.post('/weekend-shifts/admin/assignments', {
        employeeId: selectedRep.employeeId,
        weekendStartDate: rosterData.weekendStartDate,
        shiftKeys: draftShiftKeys,
      });
      setActionResult({
        success: true,
        message: response.data?.message || `Shifts saved for ${selectedRep.name}.`,
      });
      await fetchRoster(rosterData.weekendStartDate);
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || `Couldn’t save ${selectedRep.name}’s shifts. Your selection is still here; try again.`,
      });
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleClearSuspension = async () => {
    if (!selectedRep || !rosterData) return;

    setClearingSuspension(true);
    setActionResult(null);
    try {
      const response = await api.post(`/weekend-shifts/admin/suspensions/${selectedRep.employeeId}/clear`);
      updateRosterSuspensionStatus(
        selectedRep.employeeId,
        response.data?.data?.suspensionStatus
      );
      setActionResult({
        success: true,
        message: response.data?.message || `${selectedRep.name}’s suspension is lifted.`,
      });
      await fetchRoster(rosterData.weekendStartDate);
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || `Couldn’t lift ${selectedRep.name}’s suspension. Try again in a minute.`,
      });
    } finally {
      setClearingSuspension(false);
    }
  };

  const handleApplySuspension = async () => {
    if (!selectedRep || !rosterData) return;

    setApplyingSuspension(true);
    setActionResult(null);
    try {
      const response = await api.post(
        `/weekend-shifts/admin/suspensions/${selectedRep.employeeId}/apply`,
        { reason: suspensionReason.trim() }
      );
      updateRosterSuspensionStatus(
        selectedRep.employeeId,
        response.data?.data?.suspensionStatus
      );
      setActionResult({
        success: true,
        message: response.data?.message || `${selectedRep.name} is suspended from weekend signups for 60 days.`,
      });
      setSuspensionReason('');
      await fetchRoster(rosterData.weekendStartDate);
    } catch (err: any) {
      setActionResult({
        success: false,
        message: err?.response?.data?.error || `Couldn’t suspend ${selectedRep.name}. Try again in a minute.`,
      });
    } finally {
      setApplyingSuspension(false);
    }
  };

  const availabilityByKey = useMemo(
    () => new Map((rosterData?.shiftAvailability || []).map((item) => [item.key, item])),
    [rosterData]
  );

  const filteredRoster = useMemo(() => {
    const roster = rosterData?.roster || [];

    switch (rosterFilter) {
      case 'missed_any':
        return roster.filter(hasMissedAny);
      case 'missed_start':
        return roster.filter(hasMissedStart);
      case 'missed_end':
        return roster.filter(hasMissedEnd);
      case 'no_shifts':
        return roster.filter((rep) => rep.shiftCount === 0);
      case 'two_plus':
        return roster.filter((rep) => rep.shiftCount >= 2);
      case 'over_cap': {
        const overCapIds = new Set(rosterData?.overCapRepIds ?? []);
        return roster.filter((rep) => overCapIds.has(rep.employeeId));
      }
      case 'suspended':
        return roster.filter((rep) => rep.suspensionStatus?.isSuspended);
      default:
        return roster;
    }
  }, [rosterData, rosterFilter]);

  const employeeSummaries = useMemo(
    () => analyticsData?.employeeSummaries || [],
    [analyticsData]
  );

  const topSignupEmployees = useMemo(
    () => [...employeeSummaries]
      .filter((employee) => employee.totalShiftSignups > 0)
      .sort((a, b) => b.totalShiftSignups - a.totalShiftSignups || a.name.localeCompare(b.name))
      .slice(0, 10),
    [employeeSummaries]
  );

  const zeroSignupEmployees = useMemo(
    () => [...employeeSummaries]
      .filter((employee) => employee.totalShiftSignups === 0)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [employeeSummaries]
  );

  const missedStartLeaders = useMemo(
    () => [...employeeSummaries]
      .filter((employee) => employee.missedStartCount > 0)
      .sort((a, b) => b.missedStartCount - a.missedStartCount || a.name.localeCompare(b.name))
      .slice(0, 10),
    [employeeSummaries]
  );

  const missedEndLeaders = useMemo(
    () => [...employeeSummaries]
      .filter((employee) => employee.missedEndCount > 0)
      .sort((a, b) => b.missedEndCount - a.missedEndCount || a.name.localeCompare(b.name))
      .slice(0, 10),
    [employeeSummaries]
  );

  /* ---------------------------------------------------------------------- */
  /* What the page is showing                                               */
  /* ---------------------------------------------------------------------- */

  // The API resolves any picked day to its Saturday; mirror that so the
  // header names the requested weekend while it loads.
  const viewedWeekendStart = getWeekendStartForYmd(selectedWeekendStart);
  const rosterMatchesView = rosterData?.weekendStartDate === viewedWeekendStart;
  // Don't show last weekend's figures under this weekend's name.
  const rosterView = rosterData && (rosterMatchesView || (!rosterLoading && !rosterError)) ? rosterData : null;
  const viewHolidayDay = rosterView?.holiday?.enabled ? rosterView.holiday.holidayDay : null;
  const weekendLabel = formatWeekendRange(rosterView?.weekendStartDate ?? viewedWeekendStart, viewHolidayDay);
  const isUpcoming = viewedWeekendStart === upcomingWeekendStart;
  const stepperLabel = isUpcoming
    ? 'Upcoming weekend'
    : formatDateRange(viewedWeekendStart, addDaysToYmd(viewedWeekendStart, 1));
  const holidayMeta = rosterView?.holiday?.enabled
    ? `${rosterView.holiday.holiday?.holidayLabel || 'Holiday'} · ${getHolidayDayLabel(rosterView.holiday.holidayDay)} shifts open`
    : null;
  const isHistory = activeTab === 'history';
  const historySpan = analyticsData
    ? formatWeekendSpan(analyticsData.startWeekendDate, analyticsData.endWeekendDate)
    : formatWeekendSpan(historyStartWeekend, historyEndWeekend);
  const historyWeekendCount = analyticsData?.summary.totalWeekends ?? countWeekends(historyStartWeekend, historyEndWeekend);
  const historyFreshness = analyticsLoading && !analyticsData
    ? 'Loading…'
    : analyticsUpdatedAt
      ? `Updated ${formatTime(analyticsUpdatedAt)}`
      : null;
  const freshness = rosterLoading && !rosterView
    ? 'Loading…'
    : rosterUpdatedAt
      ? `Updated ${formatTime(rosterUpdatedAt)}`
      : null;

  // The ops summary and team digest always cover the upcoming weekend.
  const upcomingPhrase = rosterData?.weekendStartDate === upcomingWeekendStart
    ? formatWeekendRange(upcomingWeekendStart, rosterData.holiday?.enabled ? rosterData.holiday.holidayDay : null)
    : `the weekend starting ${formatDate(upcomingWeekendStart, { weekday: true })}`;

  const kpis = useMemo(() => {
    if (!rosterView) return null;
    const { summary, shiftAvailability } = rosterView;
    const capped = shiftAvailability.filter((item) => !item.uncapped && item.capacity !== null);
    const capacity = sum(capped.map((item) => item.capacity ?? 0));
    const filled = sum(capped.map((item) => Math.min(item.count, item.capacity ?? item.count)));
    const open = sum(capped.map((item) => item.remaining ?? 0));
    const fullShifts = capped.filter((item) => item.isFull).length;
    const holidaySignups = sum(shiftAvailability.filter((item) => item.uncapped).map((item) => item.count));
    const totalSignups = sum(Object.values(summary.shiftCounts || {}));
    const confirmed = sum(Object.values(summary.confirmedShiftCounts || {})) + sum(Object.values(summary.endConfirmedShiftCounts || {}));
    const missed = (summary.missedStartCount || 0) + (summary.missedEndCount || 0);
    const expected = totalSignups * 2;
    // Check-ins whose window has opened; before Saturday nothing is due yet.
    const due = sum(rosterView.roster.map(countDueCheckpoints));
    return { capacity, filled, open, fullShifts, holidaySignups, totalSignups, confirmed, missed, expected, due };
  }, [rosterView]);
  const kpiPending = !rosterView && rosterLoading;

  const shortLabelByKey = useMemo(
    () => new Map((rosterData?.shiftOptions ?? []).map((option) => [option.key, option.shortLabel])),
    [rosterData]
  );

  const anySending = sendingOps || sendingPolicyUpdate || sendingManagerDigest;
  const saveDisabled = !selectedRep
    || (
      Boolean(selectedRep.suspensionStatus?.isSuspended)
      && !(
        selectedRep.selectedShiftKeys.length > 0
        && draftShiftKeys.length === 0
      )
    )
    || draftShiftKeys.filter((key) => !isUncappedHolidayShiftKey(key)).length > (rosterData?.standardShiftCap ?? 2)
    || savingAssignment
    || applyingSuspension
    || clearingSuspension;

  const handleRequestSave = () => {
    if (!selectedRep) return;
    if (draftShiftKeys.length === 0 && selectedRep.selectedShiftKeys.length > 0) {
      setRepConfirm('removeShifts');
      return;
    }
    handleSaveAssignment();
  };

  const holidayDayWord = getHolidayDayLabel(rosterData?.holiday?.holidayDay);
  const holidaySignupCount = sum((rosterData?.shiftAvailability ?? []).filter((item) => item.uncapped).map((item) => item.count));

  const openEmailAction = (action: () => void) => {
    setEmailMenuAnchor(null);
    action();
  };

  const tabLabel = (text: string, count?: number) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {text}
      {typeof count === 'number' && <CountBadge count={count} max={999} />}
    </Box>
  );

  const tabProps = (tab: ManagerTab) => ({
    value: tab,
    id: `wsm-tab-${tab}`,
    // Only the active panel is mounted, so only it can be controlled.
    'aria-controls': activeTab === tab ? `wsm-panel-${tab}` : undefined,
  });
  const tabPanelProps = (tab: ManagerTab) => ({
    role: 'tabpanel',
    id: `wsm-panel-${tab}`,
    'aria-labelledby': `wsm-tab-${tab}`,
  });

  const noSignups = Boolean(kpis && kpis.totalSignups === 0);
  const noneDue = Boolean(kpis && kpis.totalSignups > 0 && kpis.due === 0);
  const checkinsUnknown = !kpis || noSignups || noneDue;
  const missedClickable = Boolean(kpis && !checkinsUnknown && kpis.missed > 0);
  const showMissedReps = () => {
    setRosterFilter('missed_any');
    setRosterShiftFilter(null);
    setActiveTab('roster');
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        overline="Attendance"
        title="Weekend shifts"
        meta={
          isHistory
            ? [`History · ${historySpan}`, formatCount(historyWeekendCount, 'weekend'), historyFreshness]
            : [weekendLabel, holidayMeta, freshness]
        }
        actions={
          <>
            {!isHistory && (
              <WeekendStepper
                label={stepperLabel}
                isUpcoming={isUpcoming}
                selectedWeekendStart={selectedWeekendStart}
                onPrevious={() => setSelectedWeekendStart(addDaysToYmd(viewedWeekendStart, -7))}
                onNext={() => setSelectedWeekendStart(addDaysToYmd(viewedWeekendStart, 7))}
                onPick={setSelectedWeekendStart}
                onUpcoming={() => setSelectedWeekendStart(upcomingWeekendStart)}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<Mail />}
              endIcon={<ChevronDown />}
              onClick={(event) => setEmailMenuAnchor(event.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(emailMenuAnchor) || undefined}
              aria-controls={emailMenuAnchor ? 'wsm-email-menu' : undefined}
              aria-busy={anySending || undefined}
            >
              {anySending ? 'Sending…' : 'Send email'}
            </Button>
            <Tooltip title="Refresh roster and history">
              <span>
                <IconButton
                  aria-label="Refresh roster and history"
                  onClick={handleRefresh}
                  disabled={anySending || rosterLoading || analyticsLoading}
                >
                  <RotateCw size={18} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Tabs
          value={activeTab}
          onChange={(_, next: ManagerTab) => setActiveTab(next)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Weekend shift sections"
        >
          <Tab {...tabProps('roster')} label={tabLabel('Roster', rosterView?.roster.length)} />
          <Tab {...tabProps('coverage')} label="Coverage and holiday" />
          <Tab {...tabProps('suspensions')} label={tabLabel('Suspensions', rosterView ? suspendedReps.length : undefined)} />
          <Tab {...tabProps('history')} label="History" />
        </Tabs>
      </PageHeader>

      <Menu
        id="wsm-email-menu"
        anchorEl={emailMenuAnchor}
        open={Boolean(emailMenuAnchor)}
        onClose={() => setEmailMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 280 } } }}
      >
        <ListSubheader>Sales reps</ListSubheader>
        <MenuItem disabled={anySending} onClick={() => openEmailAction(() => setEmailConfirm('policy'))}>
          <ListItemIcon><Send /></ListItemIcon>
          Email the policy update…
        </MenuItem>
        <MenuItem disabled={anySending} onClick={() => openEmailAction(() => setTestEmailKind('policy'))}>
          <ListItemIcon><FlaskConical /></ListItemIcon>
          Send a policy update test…
        </MenuItem>
        <Divider />
        <ListSubheader>Sales managers</ListSubheader>
        <MenuItem disabled={anySending} onClick={() => openEmailAction(() => setEmailConfirm('digest'))}>
          <ListItemIcon><Send /></ListItemIcon>
          Email each manager their team digest…
        </MenuItem>
        <MenuItem disabled={anySending} onClick={() => openEmailAction(() => setTestEmailKind('digest'))}>
          <ListItemIcon><FlaskConical /></ListItemIcon>
          Send a sample team digest…
        </MenuItem>
        <Divider />
        <ListSubheader>Ops</ListSubheader>
        <MenuItem disabled={anySending} onClick={() => openEmailAction(() => setEmailConfirm('ops'))}>
          <ListItemIcon><Send /></ListItemIcon>
          Send the ops summary to Tom and Emma…
        </MenuItem>
      </Menu>

      {/* The weekend banners and KPI row belong to the weekend tabs; History has its own range. */}
      {!isHistory && (
        <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
          {!isUpcoming && (
            <Alert
              severity="info"
              action={
                <Button size="small" variant="text" color="inherit" onClick={() => setSelectedWeekendStart(upcomingWeekendStart)}>
                  Go to upcoming weekend
                </Button>
              }
            >
              {`You’re viewing ${weekendLabel}. The ops summary and team digest always cover the upcoming weekend, starting ${formatDate(upcomingWeekendStart, { weekday: true })}.`}
            </Alert>
          )}
          {rosterError && rosterView && (
            <Alert
              severity="error"
              action={
                <Button size="small" variant="text" color="inherit" onClick={() => fetchRoster(selectedWeekendStart)}>
                  Try again
                </Button>
              }
            >
              {`Couldn’t refresh the roster. ${asSentence(rosterError)} Showing what loaded${rosterUpdatedAt ? ` at ${formatTime(rosterUpdatedAt)}` : ''}.`}
            </Alert>
          )}

          <KpiGrid sx={{ mb: 0 }}>
            <KpiTile
              label="Reps signed up"
              loading={kpiPending}
              value={rosterView ? formatNumber(rosterView.summary.totalSignedUp) : MISSING}
              caption={
                rosterView
                  ? `of ${formatCount(rosterView.summary.totalSalesReps, 'rep')} · ${formatNumber(rosterView.summary.totalWithoutShifts)} with no shifts`
                  : weekendLabel
              }
            />
            <KpiTile
              label="Sat–Sun spots filled"
              loading={kpiPending}
              value={kpis && kpis.capacity > 0 ? formatNumber(kpis.filled) : MISSING}
              caption={
                !kpis
                  ? weekendLabel
                  : kpis.capacity === 0
                    ? 'No Saturday or Sunday shifts'
                    : [
                        `of ${formatCount(kpis.capacity, 'spot')}`,
                        `${formatNumber(kpis.open)} open`,
                        viewHolidayDay
                          ? formatCount(kpis.holidaySignups, 'holiday signup')
                          : kpis.fullShifts > 0
                            ? `${formatCount(kpis.fullShifts, 'shift')} full`
                            : null,
                      ].filter(Boolean).join(' · ')
              }
              info="Saturday and Sunday shifts have a cap per shift. Holiday shifts have no cap and aren’t counted here."
            />
            <KpiTile
              label="Check-ins confirmed"
              loading={kpiPending}
              value={kpis && !checkinsUnknown ? formatNumber(kpis.confirmed) : MISSING}
              caption={
                !kpis
                  ? weekendLabel
                  : noSignups
                    ? 'No shifts signed up yet'
                    : noneDue
                      ? `None due yet · ${formatCount(kpis.expected, 'check-in')} expected`
                      : kpis.due < kpis.expected
                        ? `of ${formatNumber(kpis.due)} due so far · ${formatNumber(kpis.expected)} in all`
                        : `of ${formatCount(kpis.expected, 'check-in')} · a start and an end per shift`
              }
            />
            <KpiTile
              label="Missed check-ins"
              loading={kpiPending}
              value={kpis && !checkinsUnknown ? formatNumber(kpis.missed) : MISSING}
              caption={
                !kpis || !rosterView
                  ? weekendLabel
                  : noSignups
                    ? 'No shifts signed up yet'
                    : noneDue
                      ? 'None due yet'
                      : kpis.missed === 0
                        ? 'No missed starts or ends'
                        : `${formatNumber(rosterView.summary.missedStartCount)} start · ${formatNumber(rosterView.summary.missedEndCount)} end · ${formatCount(rosterView.summary.missedRepCount, 'rep')}`
              }
              onClick={missedClickable ? showMissedReps : undefined}
              aria-label={
                missedClickable && kpis
                  ? `Missed check-ins: ${formatNumber(kpis.missed)}. Show reps who missed a check-in.`
                  : undefined
              }
            />
          </KpiGrid>
        </Box>
      )}

      {activeTab === 'roster' && (
        <Box {...tabPanelProps('roster')}>
          <RosterTab
            rosterData={rosterView}
            loading={rosterLoading}
            error={rosterError}
            onRetry={() => fetchRoster(selectedWeekendStart)}
            weekendLabel={weekendLabel}
            filteredRoster={rosterView ? filteredRoster : []}
            filter={rosterFilter}
            onFilterChange={setRosterFilter}
            shiftFilter={rosterShiftFilter}
            onShiftFilterChange={setRosterShiftFilter}
            onOpenRep={handleOpenRep}
          />
        </Box>
      )}

      {activeTab === 'coverage' && (
        <Box {...tabPanelProps('coverage')}>
          <CoverageTab
            rosterData={rosterView}
            loading={rosterLoading}
            error={rosterError}
            onRetry={() => fetchRoster(selectedWeekendStart)}
            weekendLabel={weekendLabel}
            availabilityByKey={availabilityByKey}
            holidayDayDraft={holidayDayDraft}
            onHolidayDayDraftChange={setHolidayDayDraft}
            holidayLabelDraft={holidayLabelDraft}
            onHolidayLabelDraftChange={setHolidayLabelDraft}
            savingHoliday={savingHoliday}
            onSaveHoliday={() => handleSetHoliday(true)}
            onRequestRemoveHoliday={() => setRepConfirm('removeHoliday')}
            onShowOverCapReps={() => {
              setRosterFilter('over_cap');
              setRosterShiftFilter(null);
              setActiveTab('roster');
            }}
            onShowShiftReps={(shiftKey) => {
              setRosterFilter('all');
              setRosterShiftFilter(shiftKey);
              setActiveTab('roster');
            }}
          />
        </Box>
      )}

      {activeTab === 'suspensions' && (
        <Box {...tabPanelProps('suspensions')}>
          <SuspensionsTab
            rosterData={rosterView}
            loading={rosterLoading}
            error={rosterError}
            onRetry={() => fetchRoster(selectedWeekendStart)}
            suspendedReps={rosterView ? suspendedReps : []}
            onOpenRep={handleOpenRep}
          />
        </Box>
      )}

      {activeTab === 'history' && (
        <Box {...tabPanelProps('history')}>
          <HistoryTab
            analyticsData={analyticsData}
            loading={analyticsLoading}
            error={analyticsError}
            onRetry={() => fetchAnalytics(historyStartWeekend, historyEndWeekend)}
            historyStartWeekend={historyStartWeekend}
            historyEndWeekend={historyEndWeekend}
            onStartChange={handleHistoryStartChange}
            onEndChange={handleHistoryEndChange}
            onQuickRange={handleQuickRange}
            onViewWeekend={(weekendStartDate) => {
              setSelectedWeekendStart(weekendStartDate);
              setActiveTab('roster');
            }}
            topSignupEmployees={topSignupEmployees}
            zeroSignupEmployees={zeroSignupEmployees}
            missedStartLeaders={missedStartLeaders}
            missedEndLeaders={missedEndLeaders}
          />
        </Box>
      )}

      <RepSheet
        rep={selectedRep}
        rosterData={rosterData}
        weekendLabel={weekendLabel}
        availabilityByKey={availabilityByKey}
        isPastWeekend={Boolean(rosterData && rosterData.weekendStartDate < upcomingWeekendStart)}
        overCap={Boolean(selectedRep && rosterData?.overCapRepIds?.includes(selectedRep.employeeId))}
        draftShiftKeys={draftShiftKeys}
        onToggleShift={handleToggleDraftShift}
        onClearDraft={() => setDraftShiftKeys([])}
        suspensionReason={suspensionReason}
        onSuspensionReasonChange={setSuspensionReason}
        savingAssignment={savingAssignment}
        applyingSuspension={applyingSuspension}
        clearingSuspension={clearingSuspension}
        saveDisabled={saveDisabled}
        onClose={handleCloseRep}
        onSave={handleRequestSave}
        onRequestSuspend={() => setRepConfirm('suspend')}
        onRequestUnsuspend={() => setRepConfirm('unsuspend')}
      />

      {/* Rep and holiday confirmations */}
      <ConfirmDialog
        open={repConfirm === 'suspend' && Boolean(selectedRep)}
        onClose={() => setRepConfirm(null)}
        onConfirm={async () => {
          await handleApplySuspension();
          setRepConfirm(null);
        }}
        title={`Suspend ${selectedRep?.name ?? 'this rep'} from weekend signups?`}
        body={`${selectedRep ? firstName(selectedRep.name) : 'They'} can’t sign up for weekend shifts for 60 days. Shifts already assigned for ${weekendLabel} stay until you remove them.`}
        confirmLabel="Suspend for 60 days"
        loading={applyingSuspension}
        loadingLabel="Suspending…"
        destructive
      />
      <ConfirmDialog
        open={repConfirm === 'unsuspend' && Boolean(selectedRep)}
        onClose={() => setRepConfirm(null)}
        onConfirm={async () => {
          await handleClearSuspension();
          setRepConfirm(null);
        }}
        title={`Lift ${selectedRep?.name ?? 'this rep'}’s suspension?`}
        body={`${selectedRep ? firstName(selectedRep.name) : 'They'} can sign up for weekend shifts again right away.`}
        confirmLabel="Lift suspension"
        loading={clearingSuspension}
        loadingLabel="Lifting suspension…"
      />
      <ConfirmDialog
        open={repConfirm === 'removeShifts' && Boolean(selectedRep)}
        onClose={() => setRepConfirm(null)}
        onConfirm={async () => {
          await handleSaveAssignment();
          setRepConfirm(null);
        }}
        title={`Remove ${selectedRep?.name ?? 'this rep'} from ${weekendLabel}?`}
        body={
          selectedRep
            ? `Their ${formatCount(selectedRep.selectedShiftKeys.length, 'shift')} (${shiftListLabel(selectedRep.selectedShiftKeys, shortLabelByKey)}) open up for other reps.`
            : undefined
        }
        confirmLabel="Remove shifts"
        loading={savingAssignment}
        loadingLabel="Removing…"
        destructive
      />
      <ConfirmDialog
        open={repConfirm === 'removeHoliday'}
        onClose={() => setRepConfirm(null)}
        onConfirm={async () => {
          await handleSetHoliday(false);
          setRepConfirm(null);
        }}
        title={`Remove the ${holidayDayWord} holiday shifts?`}
        body={`${holidayDayWord} AM and PM shifts close for ${weekendLabel}.${
          holidaySignupCount > 0 ? ` ${formatCount(holidaySignupCount, 'signup is', 'signups are')} on those shifts now.` : ''
        }`}
        confirmLabel="Remove holiday shifts"
        loading={savingHoliday}
        loadingLabel="Removing…"
        destructive
      />

      {/* Email confirmations */}
      <ConfirmDialog
        open={emailConfirm === 'policy'}
        onClose={() => setEmailConfirm(null)}
        onConfirm={async () => {
          await handleSendPolicyUpdate();
          setEmailConfirm(null);
        }}
        title="Email the policy update to every sales rep?"
        body="Every active sales rep gets the weekend shift policy: a 2-shift cap on Saturday and Sunday, 13 reps per Saturday or Sunday shift, and uncapped holiday shifts."
        confirmLabel="Email all reps"
        loading={sendingPolicyUpdate}
        loadingLabel="Sending…"
      />
      <ConfirmDialog
        open={emailConfirm === 'digest'}
        onClose={() => setEmailConfirm(null)}
        onConfirm={async () => {
          await handleSendManagerDigest();
          setEmailConfirm(null);
        }}
        title="Email every sales manager their team digest?"
        body={`Each manager gets weekend coverage for ${upcomingPhrase}, showing only their direct reports and themselves.`}
        confirmLabel="Email managers"
        loading={sendingManagerDigest}
        loadingLabel="Sending…"
      />
      <ConfirmDialog
        open={emailConfirm === 'ops'}
        onClose={() => setEmailConfirm(null)}
        onConfirm={async () => {
          await handleSendOpsSummary();
          setEmailConfirm(null);
        }}
        title="Send the ops summary to Tom and Emma?"
        body={`It covers ${upcomingPhrase}${isUpcoming ? '' : ', not the weekend you’re viewing'}.`}
        confirmLabel="Send ops summary"
        loading={sendingOps}
        loadingLabel="Sending…"
      />
      <TestEmailDialog
        kind={testEmailKind}
        address={testEmailAddress}
        onAddressChange={setTestEmailAddress}
        sending={testEmailKind === 'policy' ? sendingPolicyUpdate : sendingManagerDigest}
        onClose={() => setTestEmailKind(null)}
        onSend={async () => {
          if (testEmailKind === 'policy') await handleSendPolicyUpdateTest(testEmailAddress);
          else await handleSendManagerDigestTest(testEmailAddress);
          setTestEmailKind(null);
        }}
      />

      {actionResult && (
        <Snackbar
          key={actionResult.message}
          open
          autoHideDuration={actionResult.success ? 5000 : null}
          onClose={(_, reason) => {
            if (reason !== 'clickaway') setActionResult(null);
          }}
        >
          <Alert
            severity={actionResult.success ? 'success' : 'error'}
            role={actionResult.success ? 'status' : 'alert'}
            onClose={() => setActionResult(null)}
          >
            {actionResult.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default WeekendShiftManager;
